"use client";
import React, { useEffect, useState } from "react";
import ConciergeOptionTab from "./ConciergeOptionTab";
import {
	ConciergeOptionCard,
	ConciergeOptionSlider,
} from "./ConciergeOptionCard";
import NoConciergeOptionCard from "./NoConciergeOptionCard";
import { usePropertyContext } from "../context";
import { usePathname, useRouter } from "next/navigation";
import client from "utils/client";
import ButtonV2 from "elements/ButtonV2";
import { useAuthContext } from "context/AuthContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import PropertyModal from "../Modal";
import Warning from "public/account/survey/concierge/warning.svg";
import CircleCheck from "public/account/survey/concierge/check-circle-green.svg";
import ConciergePayment from "../Survey/questions/ConciergePayment";
import { useConciergePaymentModalFlag } from "./ConciergePaymentModal";
import ConciergeHeader from "public/account/survey/concierge/concierge-header.svg";
import ConciergeHeaderBG from "public/account/survey/concierge/concierge-header-bg.png";
import Image from "next/image";
import ConciergeConfirmationContent from "./ConciergeConfirmation";
import formatDollar from "utils/formatDollar";

import BackIcon from "public/account/top-nav-mobile-back.svg";

import CalendarIcon from "public/account/concierge/v2/Calendar.svg";
import CheckGreyIcon from "public/account/concierge/v2/check-grey.svg";
import CheckNothernIcon from "public/account/concierge/v2/check-nother-lights.svg";
import ShieldIcon from "public/account/concierge/v2/shield.svg";
import ReceiptIcon from "public/account/concierge/v2/receipt.svg";
import SupportIcon from "public/account/concierge/v2/support.svg";
import ComputerIcon from "public/account/concierge/v2/computer.svg";
import ChatIcon from "public/account/concierge/v2/chat.svg";
import PiggyBankIcon from "public/account/concierge/v2/piggy-bank.svg";
import ConciergeGuy from "public/account/concierge/v2/concierge-guy-v2.svg";
import HomeIcon from "public/account/concierge/v2/home.svg";
import CheckMarkCircle from "public/account/concierge/v2/checkmark-circle.svg";

import { useResponsiveContext } from "context/ResponsiveContext";
export const DEFAULT_CONTINGENCY_PERCENTAGE = 25;
export const DEFAULT_CONCIERGE_PLAN_PRICE_ANNUALLY = 199;
export const DEFAULT_CONCIERGE_PLAN_PRICE_MONTHLY = 20;
export const DEFAULT_CONCIERGE_PLAN_PRICE_MIN_ANNUALLY = 99;
export const DEFAULT_CONCIERGE_PLAN_PRICE_MAX_ANNUALLY = 299;
export const DEFAULT_CONCIERGE_PLAN_PRICE_MIN_MONTHLY = 10;
export const DEFAULT_CONCIERGE_PLAN_PRICE_MAX_MONTHLY = 30;

export interface IConciergeContext {
	planPrice: number;
	setPlanPrice: React.Dispatch<React.SetStateAction<number>>;
	sliderMin: number;
	setSliderMin: React.Dispatch<React.SetStateAction<number>>;
	sliderMax: number;
	setSliderMax: React.Dispatch<React.SetStateAction<number>>;
	billingCycle: string;
	setBillingCycle: React.Dispatch<React.SetStateAction<string>>;
	conciergePlan: string;
	setConciergePlan: React.Dispatch<React.SetStateAction<string>>;
	conciergePaid: boolean;
	submitPayment: (args: {
		card_number: string;
		card_cvc: string;
		card_exp_month: string;
		card_exp_year: string;
		conciergeAddOnSelected?: boolean;
		extraAmount?: number;
	}) => Promise<void>;
	paymentConfirmed: boolean;
	paymentErrored: boolean;
	setPaymentErrored: React.Dispatch<React.SetStateAction<boolean>>;
	paymentSubmitting: boolean;
	cancelSubscription: () => Promise<void>;
	cancellationConfirmed: boolean;
	cancellationErrored: boolean;
	cancelling: boolean;
	downgradeInterestedTier: () => Promise<void>;
	downgradingConfirmed: boolean;
	downgradingErrored: boolean;
	downgrading: boolean;
	handleConciergePlanSelect: () => Promise<void>;
}

export const ConciergeContext = React.createContext<IConciergeContext>({
	planPrice: DEFAULT_CONCIERGE_PLAN_PRICE_ANNUALLY,
	setPlanPrice: () => {},
	sliderMin: DEFAULT_CONCIERGE_PLAN_PRICE_MIN_ANNUALLY,
	setSliderMin: () => {},
	sliderMax: DEFAULT_CONCIERGE_PLAN_PRICE_MAX_ANNUALLY,
	setSliderMax: () => {},
	billingCycle: "annually",
	setBillingCycle: () => {},
	conciergePlan: "",
	setConciergePlan: () => {},
	conciergePaid: false,
	submitPayment: async (args: {
		card_number: string;
		card_cvc: string;
		card_exp_month: string;
		card_exp_year: string;
		conciergeAddOnSelected?: boolean;
		extraAmount?: number;
	}) => {},
	paymentConfirmed: false,
	paymentErrored: false,
	setPaymentErrored: () => {},
	paymentSubmitting: false,
	cancelSubscription: async () => {},
	cancellationConfirmed: false,
	cancellationErrored: false,
	cancelling: false,
	downgradeInterestedTier: async () => {},
	downgradingConfirmed: false,
	downgradingErrored: false,
	downgrading: false,
	handleConciergePlanSelect: async () => {},
});

export const useConciergeContext = () => React.useContext(ConciergeContext);

export const ConciergeContextProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const { userData, setUserData } = useAuthContext();

	const { showConciergePaymentModalFlag, setShowConciergePaymentModalFlag } =
		useConciergePaymentModalFlag();

	const [planPrice, setPlanPrice] = useState(199);
	const [sliderMin, setSliderMin] = useState(99);
	const [sliderMax, setSliderMax] = useState(299);
	const [conciergePlan, setConciergePlan] = useState("");
	const [billingCycle, setBillingCycle] = useState("annually");
	const [conciergePaid, setConciergePaid] = useState(false);

	const [cancellationConfirmed, setCancellationConfirmed] = useState(false);
	const [cancellationErrored, setCancellationErrored] = useState(false);
	const [cancelling, setCancelling] = useState(false);

	const [downgradingErrored, setDowngradingErrored] = useState(false);
	const [downgradingConfirmed, setDowngradingConfirmed] = useState(false);
	const [downgrading, setDowngrading] = useState(false);

	const [paymentConfirmed, setPaymentConfirmed] = useState(false);
	const [paymentErrored, setPaymentErrored] = useState(false);
	const [paymentSubmitting, setPaymentSubmitting] = useState(false);

	const trackEvent = useEventTracker();
	const router = useRouter();
	const { details } = usePropertyContext();

	useEffect(() => {
		const activeConciergeSubsription = userData?.active_subscription.find(sub =>
			sub.product.includes("concierge")
		);
		if (activeConciergeSubsription) {
			setConciergePaid(true);
		} else {
			setConciergePaid(false);
		}
	}, [userData?.active_subscription]);

	useEffect(() => {
		if (downgradingConfirmed || paymentConfirmed) {
			setShowConciergePaymentModalFlag(false);
		}
	}, [downgradingConfirmed, paymentConfirmed]);

	useEffect(() => {
		if (billingCycle == "annually") {
			setSliderMin(DEFAULT_CONCIERGE_PLAN_PRICE_MIN_ANNUALLY);
			setSliderMax(DEFAULT_CONCIERGE_PLAN_PRICE_MAX_ANNUALLY);
			setPlanPrice(DEFAULT_CONCIERGE_PLAN_PRICE_ANNUALLY);
		} else {
			setSliderMin(DEFAULT_CONCIERGE_PLAN_PRICE_MIN_MONTHLY);
			setSliderMax(DEFAULT_CONCIERGE_PLAN_PRICE_MAX_MONTHLY);
			setPlanPrice(DEFAULT_CONCIERGE_PLAN_PRICE_MONTHLY);
		}
	}, [billingCycle]);

	const submitPayment = async (args: {
		card_number: string;
		card_cvc: string;
		card_exp_month: string;
		card_exp_year: string;
		conciergeAddOnSelected?: boolean;
		extraAmount?: number;
	}) => {
		try {
			setPaymentSubmitting(true);
			const response = await client.payConciergeInvoice({
				product: conciergePlan,
				cadence: billingCycle,
				card_number: args.card_number,
				card_cvc: args.card_cvc,
				card_exp_month: args.card_exp_month,
				card_exp_year: args.card_exp_year,
				plan_price: planPrice,
			});
			if (response.status !== 200) {
				setPaymentErrored(true);
				console.error("Failed to pay invoice");
				return;
			} else {
				const userData = await client.getUserData();
				setUserData(userData);
				setPaymentConfirmed(true);
				setConciergePaid(true);
			}
		} catch (err) {
			setPaymentErrored(true);
			console.error(err);
		} finally {
			setPaymentSubmitting(false);
		}
	};

	const cancelSubscription = async () => {
		const subscriptionId =
			userData?.active_subscription.find(sub =>
				sub.product.includes("concierge")
			)?.subscriptionId || null;
		if (subscriptionId) {
			setCancelling(true);
			//cancelling
			try {
				const response = await client.cancelConciergeSubsription({
					subscription_id: subscriptionId,
				});
				if (response.status === 200) {
					const userData = await client.getUserData();
					setUserData(userData);
					setCancellationConfirmed(true);
					trackEvent({
						eventName:
							constants.EVENTS.Concierge_cancellation_after_payment_action,
						data: {
							CustomerId: userData?.id,
							action: "Yes Cancel",
						},
					});
				} else setCancellationErrored(true);
			} catch (error) {
				setCancellationErrored(true);
			} finally {
				setCancelling(false);
			}
		}
	};

	const downgradeInterestedTier = async () => {
		try {
			setDowngrading(true);
			const response = await client.downgradeInterestedTier();
			if (response.status === 200) {
				const userData = await client.getUserData();
				setUserData(userData);
				setDowngradingConfirmed(true);
				trackEvent({
					eventName:
						constants.EVENTS.Concierge_onboarding_upgrade_payment_action,
					data: {
						CustomerId: userData?.id,
						action: "cancel",
					},
				});
			} else setDowngradingErrored(true);
		} catch (error) {
			setDowngradingErrored(true);
			console.error("Error downgrading account tier", error);
		} finally {
			setDowngrading(false);
		}
	};

	const handleConciergePlanSelect = async () => {
		setConciergePlan("concierge");
		await client.upgradeInterestedTier({
			interestedTier: "concierge",
		});
		const userData = await client.getUserData();
		setUserData(userData);
	};

	return (
		<ConciergeContext.Provider
			value={{
				billingCycle,
				setBillingCycle,
				conciergePlan,
				setConciergePlan,
				conciergePaid,
				submitPayment,
				paymentConfirmed,
				paymentErrored,
				setPaymentErrored,
				paymentSubmitting,
				cancelSubscription,
				cancellationConfirmed,
				cancellationErrored,
				cancelling,
				downgradeInterestedTier,
				downgradingConfirmed,
				downgradingErrored,
				downgrading,
				handleConciergePlanSelect,
				planPrice,
				setPlanPrice,
				sliderMin,
				setSliderMin,
				sliderMax,
				setSliderMax,
			}}>
			{children}
		</ConciergeContext.Provider>
	);
};

export const ConciergeOptionsCards = ({
	onOptionSelect,
	handleSkip,
	hideCta = false,
}: {
	onOptionSelect: () => void;
	handleSkip: () => void;
	hideCta?: boolean;
}) => {
	const { details } = usePropertyContext();
	const { planPrice } = useConciergeContext();
	return (
		<>
			<div className="concierge-options-cards">
				<NoConciergeOptionCard
					contingencyPercentage={
						details?.contingency
							? details?.contingency
							: DEFAULT_CONTINGENCY_PERCENTAGE
					}
					onOptionSelect={handleSkip}
					hideCTA={hideCta}
				/>
				<ConciergeOptionCard
					contingencyPercentage={
						details?.contingency
							? details?.contingency
							: DEFAULT_CONTINGENCY_PERCENTAGE
					}
					onOptionSelect={onOptionSelect}
					onStayWithBasicSelect={handleSkip}
					hideCTA={hideCta}>
					<p className="azure bold text-center" style={{ fontSize: "48px" }}>
						{formatDollar(planPrice)}
						<span className="extra-small azure">/year</span>
					</p>
					<p
						className="h4 azure text-center mb-1"
						style={{ lineHeight: "30px" }}>
						+
					</p>
				</ConciergeOptionCard>
			</div>
		</>
	);
};

export const ConciergePageContent: React.FC<{
	handleConciergePlanSelect: () => void;
	handleSkip: () => void;
	showAuroraBorealis?: boolean;
}> = ({ handleConciergePlanSelect, handleSkip, showAuroraBorealis = true }) => {
	const { details } = usePropertyContext();
	const { isMobile } = useResponsiveContext();

	return (
		<div id="concierge-page-v2-content">
			<div className="concierge-header">
				{showAuroraBorealis && (
					<>
						<div className="nothern-light-gradient-wrapper">
							<Image
								src={ConciergeHeaderBG}
								alt="Concierge Header Background"
							/>
						</div>
					</>
				)}
				<div className="concierge-header-content mt-2">
					<div className="left-column">
						<div className="concierge-header-img-wrapper">
							<ConciergeHeader className="concierge-header-img" />
						</div>
						<p className="xl bold">We do more than property taxes!</p>
						<p
							className="h4 nothern-lights-text"
							style={{ lineHeight: "1.4em" }}>
							Save More Money With Ownwell Concierge
						</p>
						<p className="denim-dark">
							Let Concierge work for you — we review and evaluate your expenses
							to find where you can save money and provide you with personalized
							recommendations.
						</p>
					</div>
					<div className="right-column">
						<ConciergeGuy className="concierge-guy-img" />
					</div>
				</div>
			</div>
			<div className="concierge-body-content">
				<div className="comparsion-table-wrapper">
					<div className="comparison-table">
						<div className="table-header-1">
							<div className="table-row">
								<div className="table-cell">
									<p> </p>
								</div>
								<div className="table-cell w-200">
									<p> </p>
								</div>
								<div className="table-cell w-250">
									<p
										className={
											(isMobile ? "tiny" : "large") +
											" semibold recommended-text nothern-lights-background"
										}>
										Recommended
									</p>
								</div>
							</div>
						</div>
						<div className="table-header-2">
							<div className="table-row">
								<div className="table-cell">
									<p> </p>
								</div>
								<div className="table-cell w-200 ownwell-text-wrapper">
									<div className="ownwell-text-wrapper-inner ownwell-text">
										<p className={(isMobile ? "small" : "large") + " bold"}>
											Ownwell
										</p>
										<p className="small ownwell-text-sub label denim_5">
											CURRENT
										</p>
									</div>
								</div>
								<div className="table-cell w-250 ownwell-concierge-text-wrapper">
									<p
										className={
											(isMobile ? "small" : "large") +
											" bold nothern-lights-text ownwell-concierge-text"
										}>
										Ownwell Concierge
									</p>
								</div>
							</div>
						</div>
						<div className="table-body">
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">Tax Appeals and Exemptions </p>
										</div>
										<p className="small feature-description denim_75">
											Never overpay on your property taxes with annual appeals
											and exemption activation.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<CheckGreyIcon className="check-icon" />
								</div>
								<div className="table-cell w-250">
									<CheckNothernIcon className="check-icon" />
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">Self Serve Ways to Save</p>
										</div>
										<p className="sm feature-description denim_75">
											Shop for new home insurance, reduce your internet costs,
											or switch electricity providers online.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<CheckGreyIcon className="check-icon" />
								</div>
								<div className="table-cell w-250">
									<CheckNothernIcon className="check-icon" />
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">
												Mortgage and Home Equity Savings
											</p>
										</div>
										<p className="small feature-description denim_75">
											We analyzes your mortgage to alert you if there are
											refinance savings, help remove PMI, get you out of escrow,
											or find compelling ways to use your home equity.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<p> </p>
								</div>
								<div className="table-cell w-250">
									<div className="flex flex-column items-center justify-center flex-gap-2">
										<CheckNothernIcon className="check-icon" />
										<div
											className="savings-container"
											style={{ padding: "2px 6px" }}>
											<p className="sm kelly-dark bold">$1,512 avg savings</p>
										</div>
									</div>
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">Insurance Rate Review</p>
										</div>
										<p className="small feature-description denim_75">
											An insurance expert performs a comprehensive review of
											your policy year-round to ensure you have the right
											coverage at the best price.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<p> </p>
								</div>
								<div className="table-cell w-250">
									<div className="flex flex-column items-center justify-center flex-gap-2">
										<CheckNothernIcon className="check-icon" />
										<div
											className="savings-container"
											style={{ padding: "2px 6px" }}>
											<p className="sm kelly-dark bold">$474 avg savings</p>
										</div>
									</div>
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">Utility Bill Reduction</p>
										</div>
										<p className="small feature-description denim_75">
											Utility bills are reviewed regularly to determine if
											you’re paying more than you should be. We’ll notify you
											when the time is right to make any change to save money.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<p> </p>
								</div>
								<div className="table-cell w-250">
									<div className="flex flex-column items-center justify-center flex-gap-2">
										<CheckNothernIcon className="check-icon" />
										<div
											className="savings-container"
											style={{ padding: "2px 6px" }}>
											<p className="sm kelly-dark bold">$522 avg savings</p>
										</div>
									</div>
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">Exemption Fall Off Monitoring</p>
										</div>
										<p className="small feature-description denim_75">
											Every few years counties audit exemptions. We will monitor
											and detect if your property's primary residence exemption
											falls off, notify you right away and help you reapply.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<p> </p>
								</div>
								<div className="table-cell w-250">
									<CheckNothernIcon className="check-icon" />
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">An Expert in Your Corner</p>
										</div>
										<p className="small feature-description denim_75">
											Get access to a dedicated Concierge expert when needed,
											while they constantly search for more savings in the
											background.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<p> </p>
								</div>
								<div className="table-cell w-250">
									<CheckNothernIcon className="check-icon" />
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div className="table-cell-content">
										<div className="table-cell-content-header">
											<p className="semibold">Contingency Fee</p>
										</div>
										<p className="small feature-description denim_75">
											Our savings-or-free contingency fee is applied to bill
											savings or refunds we achieve.
										</p>
									</div>
								</div>
								<div className="table-cell w-200">
									<p
										className={
											(isMobile ? "small" : "large") +
											" semibold denim_5 text-center"
										}>
										{details?.contingency ?? DEFAULT_CONTINGENCY_PERCENTAGE}% of
										savings
									</p>
								</div>
								<div className="table-cell w-250">
									<p
										className={
											(isMobile ? "small" : "large") +
											" semibold denim_5 text-center"
										}>
										{details?.contingency ?? DEFAULT_CONTINGENCY_PERCENTAGE}% of
										savings
									</p>
								</div>
							</div>
							<div className="table-row">
								<div className="table-cell">
									<div
										className="savings-container flex-column mt-1 w-100"
										style={{
											justifyContent: "center",
										}}>
										<div className="flex items-center justify-start w-100">
											<ShieldIcon className="icon" />
											<p className="small bold kelly-dark">
												Savings Guaranteed or your money back
											</p>
										</div>
										<p className="small kelly-dark w-100">
											We’ll save you more than our yearly Concierge fee, or your
											money back!
										</p>
									</div>
								</div>
								<div className="table-cell w-200 items-center-top">
									{isMobile ? (
										<div className="cta-container">
											<p className="small semibold denim_5 text-center">
												Included
											</p>
										</div>
									) : (
										<div className="cta-container">
											<p className="large semibold denim_5 text-center">
												Included
											</p>
											<ButtonV2
												size="default"
												variant="primary-outline"
												onClick={handleSkip}>
												Continue
											</ButtonV2>
										</div>
									)}
								</div>
								<div className="table-cell w-250 custom-border">
									{isMobile ? (
										<div className="cta-container">
											<p className="small semibold azure text-center">
												Pay What You Think Is Fair
											</p>
										</div>
									) : (
										<div className="cta-container">
											<p className="semibold azure text-center">
												Pay What You Think Is Fair
											</p>
											<ButtonV2
												size="default"
												variant="primary"
												onClick={handleConciergePlanSelect}>
												Add Concierge
											</ButtonV2>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
				{isMobile && (
					<div className="concierge-footer-mobile">
						<div className="savings-container">
							<ShieldIcon className="icon" />
							<p className="small kelly-dark">
								We’ll save you more than our yearly Concierge fee, or your money
								back!
							</p>
						</div>
						<ButtonV2
							size="default"
							variant="primary"
							onClick={handleConciergePlanSelect}>
							Add Concierge
						</ButtonV2>
						<ButtonV2
							size="default"
							variant="primary-outline"
							onClick={handleSkip}>
							Continue Without Concierge
						</ButtonV2>
					</div>
				)}
			</div>
		</div>
	);
};

const ConciergePage = () => {
	const { userData, setUserData } = useAuthContext();
	const { details } = usePropertyContext();

	const {
		conciergePlan,
		setConciergePlan,
		billingCycle,
		setBillingCycle,
		conciergePaid,
		cancelling,
		cancellationConfirmed,
		cancellationErrored,
		cancelSubscription,
		handleConciergePlanSelect,
	} = useConciergeContext();

	const [cancelModalOpen, setCancelModalOpen] = useState(false);

	const router = useRouter();

	const trackEvent = useEventTracker();

	useEffect(() => {
		trackEvent({
			eventName: constants.EVENTS.Concierge_upgrade_page_viewed,
			data: {
				CustomerId: userData?.id,
			},
		});
		const internalScrollingComp = document.getElementById(
			"property-layout-content"
		);
		if (internalScrollingComp) {
			internalScrollingComp.scrollTo(0, 0);
		}
	}, []);

	const handleSkip = () => {
		router.push(`/account/properties/${details?.id}`);
	};

	const showOverviewPage = () => {
		router.push(`/account/properties/${details?.id}`);
	};

	const handlePaymentSuccess = async () => {
		const userData = await client.getUserData();
		setUserData(userData);
		trackEvent({
			eventName: constants.EVENTS.Concierge_upgrade_payment_action,
			data: {
				CustomerId: userData?.id,
				action: "submit",
			},
		});
	};

	const openCancellationModal = () => {
		setCancelModalOpen(true);
		trackEvent({
			eventName: constants.EVENTS.Concierge_cancellation_after_payment_viewed,
			data: {
				CustomerId: userData?.id,
			},
		});
	};

	const getConciergePageContent = () => {
		if (conciergePaid) {
			return (
				<>
					<ConciergeConfirmationContent />
					<div className="btn-container">
						<ButtonV2 size="small" variant="primary" onClick={showOverviewPage}>
							Go To Overview
						</ButtonV2>
						<p
							className="cancel-subscription sm"
							onClick={openCancellationModal}>
							Cancel concierge service
						</p>
					</div>
				</>
			);
		} else if (
			(!!conciergePlan && !!billingCycle) ||
			userData?.interested_tier?.includes("concierge")
		) {
			return (
				<>
					<div className="concierge-page-payment-wrapper mt-3">
						<ConciergePayment handleContinueClick={handlePaymentSuccess} />
					</div>
				</>
			);
		} else {
			return (
				<ConciergePageContent
					handleConciergePlanSelect={handleConciergePlanSelect}
					handleSkip={handleSkip}
				/>
			);
		}
	};

	const dontCancelSubsciption = () => {
		setCancelModalOpen(false);
		trackEvent({
			eventName: constants.EVENTS.Concierge_cancellation_after_payment_action,
			data: {
				CustomerId: userData?.id,
				action: "No Keep",
			},
		});
	};

	return (
		<div className="concierge-page-wrapper">
			{getConciergePageContent()}
			{cancelModalOpen && (
				<PropertyModal onAttemptExit={() => setCancelModalOpen(false)}>
					<div className="cancel-subsciption-modal-wrapper">
						<div className="confirmation-modal-wrapper">
							{cancellationConfirmed ? (
								<>
									<div className="warning-img-wrapper">
										<CircleCheck />
									</div>
									<p className="xl bold">Membership Canceled</p>
									<ButtonV2
										size="small"
										variant="primary"
										className="close-btn"
										onClick={showOverviewPage}>
										Close
									</ButtonV2>
								</>
							) : (
								<>
									<div className="warning-img-wrapper">
										<Warning />
									</div>
									<p className="xl bold">Cancel Concierge Services?</p>
									<p className="sm text-center">
										You will lose access to Concierge features, services and
										support. If we've achieved successful savings already from a
										service with a contingecy fee such as appeals, exemption
										refunds or bill reductions, you will still be expected to
										pay those invoices.
									</p>
									{cancellationErrored && (
										<p className="rust">
											There was an error cancelling your subscription. Please
											try again.
										</p>
									)}
									<div className="btn-container">
										<ButtonV2
											size="small"
											variant="destructive-outline"
											onClick={cancelSubscription}
											disabled={cancelling}>
											{cancelling ? "Cancelling..." : "Yes, Cancel Concierge"}
										</ButtonV2>
										<ButtonV2
											size="small"
											variant="primary-outline"
											onClick={dontCancelSubsciption}>
											No, Keep Finding Savings
										</ButtonV2>
									</div>
								</>
							)}
						</div>
					</div>
				</PropertyModal>
			)}
		</div>
	);
};

export default function Page() {
	return (
		<ConciergeContextProvider>
			<ConciergePage />
		</ConciergeContextProvider>
	);
}
